import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Share2, Loader2, Plug, RefreshCw, Wand2, CheckCircle2, Star } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { UpgradeCard, PastDueBanner, PausedBanner } from "@/components/UpgradeCard";
import { HygieneNote } from "@/components/HygieneNote";

const PLATFORMS = [
  { key: "facebook", label: "Facebook" },
  { key: "instagram", label: "Instagram" },
  { key: "googlebusiness", label: "Google Business" },
  { key: "linkedin", label: "LinkedIn" },
];

export function SocialDialog({ siteId }) {
  const [open, setOpen] = useState(false);
  const [social, setSocial] = useState(null);
  const [ent, setEnt] = useState(null);
  const [keyOk, setKeyOk] = useState(false);
  const [busy, setBusy] = useState("");
  const [plan, setPlan] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [hyg, setHyg] = useState({});           // WO9: cleaning note, flags, facts per draft

  const load = () => api.get(`/sites/${siteId}/social`).then((r) => {
    setSocial(r.data.social); setKeyOk(r.data.key_configured); setEnt(r.data.entitlement);
  }).catch(() => {});
  useEffect(() => { if (open) load(); }, [open]);

  const act = async (label, fn) => {
    setBusy(label);
    try { await fn(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(""); }
  };

  const connect = (p) => act("c" + p, async () => {
    const { data } = await api.get(`/sites/${siteId}/social/connect/${p}`);
    if (data.authUrl) window.open(data.authUrl, "_blank");
    toast.message("Complete the connection in the new tab, then Sync accounts.");
  });
  const sync = () => act("sync", async () => { const { data } = await api.post(`/sites/${siteId}/social/sync-accounts`); await load(); toast.success(`${data.accounts.length} account(s) synced`); });
  const genPlan = () => act("plan", async () => { const { data } = await api.post(`/sites/${siteId}/social/plan`); setPlan(data); toast.success(`Drafted ${data.posts.length} posts for review`); });
  const approve = () => act("approve", async () => { const { data } = await api.post(`/sites/${siteId}/social/plan/${plan.id}/approve`); toast.success(`Scheduled ${data.scheduled}/${data.total} posts`); setPlan(null); });
  const loadReviews = () => act("reviews", async () => { const { data } = await api.get(`/sites/${siteId}/social/gbp-reviews`); setReviews(data.reviews || []); });
  const draftReply = (rev) => act("d" + rev.id, async () => { const { data } = await api.post(`/sites/${siteId}/social/gbp-reviews/${rev.id}/reply-draft`, { review_text: rev.text, rating: rev.rating }); setDrafts((d) => ({ ...d, [rev.id]: data.draft })); setHyg((h) => ({ ...h, [rev.id]: { ...(data.hygiene || {}), facts_changed: data.facts_changed || [], original: data.draft } })); });
  const postReply = (rev) => act("p" + rev.id, async () => { await api.post(`/sites/${siteId}/social/gbp-reviews/${rev.id}/reply`, { comment: drafts[rev.id] }); toast.success("Reply posted"); });
  const blockedReply = (rev) => { const h = hyg[rev.id]; return !!h?.facts_changed?.length && drafts[rev.id] === h.original; };

  const entitled = (ent?.tier || 0) >= 2;
  const hasGBP = (social?.accounts || []).some((a) => a.platform === "googlebusiness");

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="social-button"><Share2 className="mr-2 h-4 w-4" /> Social{entitled ? " ✓" : ""}</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="social-dialog">
        <DialogHeader>
          <DialogTitle>Social & Google Business</DialogTitle>
          <DialogDescription>Paid add-on. Posts are drafted for your review before anything publishes.</DialogDescription>
        </DialogHeader>

        {!keyOk && <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-600 dark:text-amber-400">Social add-on is not activated on this server yet (no Zernio key).</div>}

        <PastDueBanner status={ent?.status} />
        <PausedBanner entitlement={ent} />

        {ent && !entitled && <UpgradeCard siteId={siteId} offer={ent.upgrade} />}

        {social && entitled && (
          <div className="space-y-5">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Connect accounts</p>
              <div className="flex flex-wrap gap-2">
                {PLATFORMS.map((p) => (
                  <Button key={p.key} size="sm" variant="outline" onClick={() => connect(p.key)} disabled={busy === "c" + p.key} data-testid={`social-connect-${p.key}`}>
                    {busy === "c" + p.key ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Plug className="mr-1.5 h-3.5 w-3.5" />}{p.label}
                  </Button>
                ))}
                <Button size="sm" variant="ghost" onClick={sync} disabled={busy === "sync"} data-testid="social-sync"><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Sync</Button>
              </div>
              {(social.accounts || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {social.accounts.map((a) => <span key={a.zernio_account_id} className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-600 dark:text-emerald-400">{a.platform}: {a.display_name || "connected"}</span>)}
                </div>
              )}
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">2-week plan (review mode)</p>
                <Button size="sm" onClick={genPlan} disabled={busy === "plan"} data-testid="social-generate-plan"><Wand2 className="mr-1.5 h-3.5 w-3.5" /> Generate</Button>
              </div>
              {plan && (
                <div className="space-y-2">
                  {plan.posts.map((p) => (
                    <div key={p.id} className="space-y-1.5 rounded-md border border-border bg-background p-2 text-sm" data-testid="social-plan-post">
                      <p>{p.content}</p>
                      <HygieneNote info={p.hygiene} factsChanged={p.facts_changed} testId={`social-hygiene-${p.id}`} />
                    </div>
                  ))}
                  <Button size="sm" className="w-full" onClick={approve} disabled={busy === "approve"} data-testid="social-approve-plan">
                    {busy === "approve" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />} Approve &amp; schedule all
                  </Button>
                  {plan.posts.some((p) => p.facts_changed?.length) && (
                    <p className="text-xs text-red-600 dark:text-red-400" data-testid="social-plan-blocked-note">
                      Posts with a changed fact are held back when you approve. Draft the plan again to replace them.
                    </p>
                  )}
                </div>
              )}
            </div>

            {hasGBP && (
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Google reviews</p>
                  <Button size="sm" variant="ghost" onClick={loadReviews} disabled={busy === "reviews"} data-testid="social-load-reviews"><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Load</Button>
                </div>
                {(reviews || []).map((rev) => (
                  <div key={rev.id} className="mb-2 rounded-md border border-border bg-background p-2 text-sm">
                    <div className="flex items-center gap-1 text-amber-500">{Array.from({ length: rev.rating || 0 }).map((_, i) => <Star key={i} className="h-3 w-3 fill-current" />)}</div>
                    <p className="mt-1">{rev.text}</p>
                    {drafts[rev.id] !== undefined ? (
                      <div className="mt-2 space-y-2">
                        <Textarea value={drafts[rev.id]} onChange={(e) => setDrafts((d) => ({ ...d, [rev.id]: e.target.value }))} className="text-sm" />
                        <HygieneNote info={hyg[rev.id]} factsChanged={hyg[rev.id]?.facts_changed} testId={`social-reply-hygiene-${rev.id}`} />
                        <Button size="sm" onClick={() => postReply(rev)} disabled={busy === "p" + rev.id || blockedReply(rev)} data-testid="social-post-reply">Post reply</Button>
                      </div>
                    ) : (
                      <Button size="sm" variant="outline" className="mt-2" onClick={() => draftReply(rev)} disabled={busy === "d" + rev.id} data-testid="social-draft-reply">Draft AI reply</Button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {ent?.can_manage && (
              <p className="border-t border-border pt-3 text-xs text-muted-foreground" data-testid="social-manage-note">
                {ent.source === "stripe" ? "Billed through Stripe." :
                  ent.bundle_name ? `Included with ${ent.bundle_name}.` : "Comped."}{" "}
                Turning this add-on off happens on the Add-ons page, where it pauses for 30 days before any
                account is disconnected.
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
